import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import { Logo } from '../Logo';
import { ThemeToggle } from '../ThemeToggle';
import { PasswordModal } from './PasswordModal';
import { useCaseStudyAccess } from '../../contexts/CaseStudyAccessContext';
import { profile } from '../../data/profile';
import { workItems } from '../../data/work';
type CaseStudyShellProps = {
  children: React.ReactNode;
};
export function CaseStudyShell({
  children
}: CaseStudyShellProps) {
  const {
    scrollYProgress
  } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 220,
    damping: 40,
    restDelta: 0.001
  });
  const {
    pathname
  } = useLocation();
  const navigate = useNavigate();
  const {
    isUnlocked,
    verify,
    grantAccess
  } = useCaseStudyAccess();

  // Normalize the path (trailing slashes, hosting prefixes) before matching, and only lock pages explicitly marked locked.
  const cleanPath = pathname.replace(/\/+$/, '');
  // Sub-pages (e.g. /work/clear-docs/review) inherit their parent case study's lock.
  const item = workItems.find(w => w.href && (cleanPath.endsWith(w.href) || cleanPath.includes(`${w.href}/`)));
  const isLocked = Boolean(item?.locked) && !isUnlocked;
  return <div className="min-h-screen w-full bg-canvas text-fg" data-id="element-96" data-mp="1d87m0m6q5naa">
      {!isLocked && <motion.div aria-hidden className="fixed inset-x-0 top-0 z-50 h-0.5 origin-left bg-coral" style={{
      scaleX
    }} data-id="element-97" data-mp="20y1w4vznsxqu" />}
      <header className="sticky top-0 z-40 bg-canvas/90 backdrop-blur" data-id="element-98" data-mp="25vk9qzomg156">
        <div className="mx-auto flex max-w-[920px] items-center justify-between px-5 py-4 md:px-8" data-id="element-99" data-mp="1r65xnjtnv5m3">
          <Link to={{
          pathname: '/',
          hash: '#work'
        }} className="group inline-flex items-center gap-1.5 text-sm font-medium text-fg-muted transition-colors duration-150 hover:text-ink" data-id="element-100" data-mp="1w58rs983rrfa">
            <ArrowLeftIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:-translate-x-0.5" data-id="element-101" data-mp="2oo5yl0142rg9" />
            All case studies
          </Link>
          <div className="flex items-center gap-3" data-id="element-102" data-mp="2tj8of9yobdm0">
            <ThemeToggle data-id="element-103" data-mp="2the15ozxj9er" />
            <Link to="/" className="flex items-center gap-2" data-id="element-104" data-mp="2phn6vbb72b5h">
              <Logo className="h-8 w-8" data-id="element-105" data-mp="2pg2dhp0m8y97" />
              <span className="hidden text-sm font-semibold text-fg sm:inline" data-id="element-106" data-mp="2kjbuastilmjk">{profile.name}</span>
            </Link>
          </div>
        </div>
      </header>

      {isLocked ? <>
          <main aria-hidden className="pointer-events-none mx-auto max-w-[920px] select-none px-5 pb-16 pt-8 blur-md md:px-8 md:pt-12" data-id="element-107" data-mp="2at91asme88l8">
            <div className="h-3 w-56 rounded-full bg-coral-soft" data-id="element-108" data-mp="25rjqne331wxo" />
            <p className="mt-5 max-w-3xl text-[34px] font-semibold leading-[1.1] tracking-tight text-ink md:text-5xl" data-id="element-109" data-mp="21tvte4dx3s2m">
              {item?.title ?? 'Case study'}
            </p>
            <div className="mt-6 max-w-2xl space-y-2.5" data-id="element-110" data-mp="1rv69k62gnxjq">
              <div className="h-3 w-full rounded-full bg-line" data-id="element-111" data-mp="1rwhfocjdphe0" />
              <div className="h-3 w-11/12 rounded-full bg-line" data-id="element-112" data-mp="7bkmmdniszqx" />
              <div className="h-3 w-3/4 rounded-full bg-line" data-id="element-113" data-mp="cae3mg20n8vu" />
            </div>
            <div className="mt-14 aspect-[16/8] rounded-2xl bg-surface" data-id="element-114" data-mp="hajo9c9vbv4f" />
          </main>
          <PasswordModal open onClose={() => navigate({
        pathname: '/',
        hash: '#work'
      })} verify={verify} onSuccess={grantAccess} data-id="element-115" data-mp="lbvdvhsqsgcm" />
        </> : <>
          <main className="mx-auto max-w-[920px] px-5 pb-16 pt-8 md:px-8 md:pt-12" data-id="element-116" data-mp="3ppadvj6vn45k">{children}</main>
          <footer className="mx-auto max-w-[920px] px-5 pb-10 text-xs text-fg-faint md:px-8" data-id="element-117" data-mp="3trf4sa5xxmoi">
            Designed with love <span aria-label="love" role="img" data-id="element-118" data-mp="2e18ddqtomyz">♥️</span> by Kat Diaz - 2026
          </footer>
        </>}
    </div>;
}